<script lang="ts">
	import { auth_locked_devices } from 'src/stores/device-runtime/local-auth';
	import { createMemberStatusSnapshot } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import {
		get_z_wave_channel_structure_from_category,
		is_dali,
		is_gen_z,
		real_id,
		get_device_component_index,
		get_gen_2_control_component_channel_structure,
		is_gen_2,
		isProThreeEM,
	} from '../../../helpers/interface_helpers';
	import {
		isXT1Module,
		isXT1ComponentDevice,
		getTemplateComponents,
	} from '../../../helpers/xt1_helpers/template_helpers';

	import { LabelList, LabelListV2 } from '@Shelly/shelly-design-system';
	import type { LabelListPropsTypes } from '@Shelly/shelly-design-system';

	import { format } from 'svelte-i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { mapIconSolidWeightToLight, mapLegacyColorToToken } from 'src/helpers/new-layout-helpers';

	export let item;
	export let size: LabelListPropsTypes['size'] = 'm';
	export let hasMissingDevice = false;

	//ToDo: fastly done need work!!!!
	const get_icon = (category, state) => {
		if (typeof state === 'undefined') {
			state = false;
		}

		switch (category) {
			case 'roller':
				return state ? 'fa fa-blinds-raised' : 'fa fa-blinds';
			case 'relay':
				return state ? 'fa fa-toggle-on' : 'fa fa-toggle-off';
			case 'light':
				return 'fa fa-lightbulb';
			case 'thermostat':
				return 'fa fa-thermometer-three-quarters';
			case 'breaker':
				return 'fa fa-power-off';
			default:
				break;
		}
	};

	const get_state = (category, device, status) => {
		// Used instead of 'device.channel' because there are devices without consistency between their control component and device channel
		const controlComponentIndex = get_device_component_index(device);
		if (category === undefined) return false;
		if (!status?.online) return undefined;
		if (isXT1Module(device?.type) && !isXT1ComponentDevice(device)) {
			const stateComponent = getTemplateComponents(device, /^boolean:/, /^(enabled?|state)$/)?.[0];
			return stateComponent?.status?.value ?? false;
		}
		if (category === 'roller') {
			let statusRoller = status['cover:0'];
			if (status.hasOwnProperty('rollers')) statusRoller = status.rollers[0];
			if (statusRoller?.current_pos > -1 && statusRoller?.current_pos < 101) {
				return statusRoller?.current_pos > 0;
			} else if (statusRoller?.last_direction === 'open') {
				return true;
			} else if (statusRoller?.last_direction === 'close') {
				return false;
			} else {
				return false;
			}
		} else if (typeof status[category.split('s_')[0] + 's'] !== 'undefined') {
			switch (category) {
				case 'thermostat':
					return status.thermostats !== undefined;
				default:
					return status[category.split('s_')[0] + 's'][
						device.category === 'emeter' ? 0 : get_device_component_index(device)
					].ison;
			}
		} else if (
			is_gen_2(device) &&
			category === 'light' &&
			typeof status[get_gen_2_control_component_channel_structure(device)] !== 'undefined'
		) {
			return status?.[get_gen_2_control_component_channel_structure(device)]?.output;
		} else if (isProThreeEM(device?.type) && typeof status[`switch:100`] !== 'undefined') {
			return status?.[`switch:100`]?.output;
		} else if (typeof status[`switch:${controlComponentIndex}`] !== 'undefined') {
			return status?.[`switch:${controlComponentIndex}`]?.output;
		} else if (typeof status[`cover:${controlComponentIndex}`] !== 'undefined') {
			return status?.[`switch:${controlComponentIndex}`]?.current_pos !== 0;
		} else if (typeof status[`cb:${controlComponentIndex}`] !== 'undefined') {
			//Adding a safety check for circuit breakers. Maybe it's not the best way to do it but at the moment it's the best I can think of
			if (status[`cb:${controlComponentIndex}`]?.safety === true) {
				status['locked'] = true;
			} else {
				delete status['locked'];
			}
			status.ison = status[`cb:${controlComponentIndex}`]?.output;
			return status;
		} else if (typeof status[`light:${controlComponentIndex}`] !== 'undefined') {
			if (is_dali(device)) {
				if (status?.dali?.errors?.length > 0 || status?.dali?.cg_count === 0) {
					return undefined;
				}
			} //status icons for DALI devices gen 3 with errors or no control gear
			return status?.[`light:${controlComponentIndex}`]?.output;
		} else if (typeof status[`cct:${controlComponentIndex}`] !== 'undefined') {
			return status?.[`cct:${controlComponentIndex}`]?.output;
		} else if (typeof status[`rgb:${controlComponentIndex}`] !== 'undefined') {
			return status?.[`rgb:${controlComponentIndex}`]?.output;
		} else if (typeof status[`rgbw:${controlComponentIndex}`] !== 'undefined') {
			return status?.[`rgbw:${controlComponentIndex}`]?.output;
		} else if (typeof status?.[device.category + 's']?.[controlComponentIndex] !== 'undefined') {
			//SHSW mode light
			return status[device.category + 's'][controlComponentIndex].ison;
		} else if (is_gen_z(device)) {
			const component = get_z_wave_channel_structure_from_category(device);
			if (device.category !== 'roller') {
				return status[component].currentValue;
			} else {
				return status?.[component]?.currentValue < 99;
			}
		} else {
			return status;
		}
	};

	$: memberStatusStore = createMemberStatusSnapshot(item?.devices ?? []);
	$: memberStatus = $memberStatusStore;

	$: labelList = item.devices
		.filter((id) => typeof $devices[id] !== 'undefined')
		.map((id) => {
			const state = get_state(item.type, $devices[id], memberStatus[real_id(id)]);
			const locked = $auth_locked_devices.indexOf(real_id(id)) !== -1;
			const isLightOn =
				typeof state !== 'undefined' &&
				((state && !Object.hasOwnProperty.call(state, 'ison')) || state?.ison);
			const iconColor =
				typeof state !== 'undefined'
					? isLightOn
						? '--color-primary'
						: '--color_scheme-color_white'
					: '--color-3';

			let icon =
				typeof state !== 'undefined'
					? locked || state?.locked
						? 'fa fa-lock'
						: get_icon(item.type, state)
					: 'fa fa-signal-slash';

			const result: any = { icon };
			if (icon === 'fa fa-lightbulb' && iconColor === '--color-primary') {
				result.icon = 'fa-duotone fa-light fa-lightbulb';
				result.iconStyle = `--fa-primary-color: var(--color-primary); --fa-secondary-color: var(--color-primary-10); --fa-secondary-opacity: 1`;
			} else if (icon === 'fa fa-toggle-on' && iconColor === '--color-primary') {
				result.icon = 'fa-duotone fa-light fa-toggle-on';
				result.iconStyle = `--fa-primary-color: var(--color-primary); --fa-secondary-color: var(--color-primary-10); --fa-secondary-opacity: 1`;
			} else {
				result.iconColor = iconColor;
			}

			return result;
		});

	$: {
		if (hasMissingDevice && labelList) {
			labelList = [
				{
					icon: 'fas fa-warning',
					value: $format('missing_device_label', { default: 'Missing Device' }),
					type: 'warning',
				},
				...labelList,
			];
		}
	}
</script>

{#if labelList.length > 0}
	{#if $showNewLayout}
		<LabelListV2
			labelList={labelList.map((el) => ({
				...el,
				icon: mapIconSolidWeightToLight(el?.icon),
				backgroundColor: mapLegacyColorToToken(el?.type),
			})) || []}
			{size}
			a11yProps={{ isInteractive: true }}
			on:label-click
		/>
	{:else}
		<LabelList {labelList} {size} on:label-click />
	{/if}
{/if}
